// VenueDashboardOrdersPage.jsx — EV-768: owner ORDER-HISTORY view.
// Shows who ordered what at this venue, with the customer's name (captured at
// pay-time on payments.orders.customer_name), newest first. The unique asset:
// no individual restaurant has a named, cross-visit payment history — this is the
// data layer the Venue CRM (EV-770) + spend-loyalty (EV-769) build on.
//
// API (member-gated, Neon): GET /api/venue-dashboard/:slug (venue id + role) ·
// GET /api/me/venues/:id/orders → { orders: [{ customer_name, amount_vnd, memo,
//   status, item_count, created_at, … }] }

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';
import { dtLocale } from '../../utils/dateLocale';

const BLUE = '#3b82f6';

function token() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') || ''
  );
}
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

const fmtVnd = (p) => {
  const n = Number(p);
  if (!p || isNaN(n)) return '—';
  return `${n.toLocaleString()}₫`;
};

// VN local time (UTC+7) — DB stores UTC.
const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(dtLocale(), {
      timeZone: 'Asia/Ho_Chi_Minh', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return iso; }
};

// CON-1606 — labelKey, not label. This table is MODULE SCOPE, so it cannot call
// a hook and it is evaluated once at import: a t() here would freeze the English
// string before the user's locale is even known. Resolved at render instead.
// Patrick saw "PAID" / "canceled" in an otherwise Vietnamese orders screen.
const STATUS_STYLE = {
  paid:      { bg: '#dcfce7', fg: '#166534', labelKey: 'vdOrders.statusPaid' },
  confirmed: { bg: '#dcfce7', fg: '#166534', labelKey: 'vdOrders.statusConfirmed' },
  completed: { bg: '#dcfce7', fg: '#166534', labelKey: 'vdOrders.statusCompleted' },
  pending:   { bg: '#fef3c7', fg: '#92400e', labelKey: 'vdOrders.statusPending' },
  created:   { bg: '#f3f4f6', fg: '#6b7280', labelKey: 'vdOrders.statusCreated' },
  cancelled: { bg: '#fee2e2', fg: '#991b1b', labelKey: 'vdOrders.statusCancelled' },
  failed:    { bg: '#fee2e2', fg: '#991b1b', labelKey: 'vdOrders.statusFailed' },
};

export default function VenueDashboardOrdersPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { t } = useTranslation();
  const base = getApiBaseUrl();

  const [venue, setVenue] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all'); // all | paid | pending

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const vRes = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, { headers: authHeaders() });
      const vj = await vRes.json();
      const v = vj?.venue || vj?.data || vj;
      if (!v?.id) throw new Error(vj?.message || 'Venue not found / no access');
      setVenue({ id: v.id, role: vj?.myRole || v.myRole || v.role });
      const oRes = await fetch(`${base}/me/venues/${v.id}/orders`, { headers: authHeaders() });
      const oj = await oRes.json();
      if (!oj?.success) throw new Error(oj?.message || oj?.error || 'Could not load orders');
      setOrders(oj.orders || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [base, slug]);

  useEffect(() => { load(); }, [load]);

  // CON-525 — staff checks their banking app, then confirms the payment here.
  // Confirming fires the automatic Lee-stamp for participating venues.
  const [confirming, setConfirming] = useState(null); // order id in flight
  const confirmPayment = async (orderId) => {
    if (!venue?.id || confirming) return;
    if (!window.confirm(t('vdOrders.confirmPrompt'))) return;
    setConfirming(orderId);
    try {
      const r = await fetch(`${base}/me/venues/${venue.id}/orders/${orderId}/confirm-payment`, {
        method: 'POST', headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) throw new Error(j?.message || 'Could not confirm');
      await load();
    } catch (e) { setErr(e.message); }
    setConfirming(null);
  };

  const isPaid = (s) => ['paid', 'confirmed', 'completed'].includes(String(s || '').toLowerCase());

  const filtered = useMemo(() => {
    if (filter === 'paid') return orders.filter((o) => isPaid(o.status) || o.confirmed);
    if (filter === 'pending') return orders.filter((o) => !isPaid(o.status) && !o.confirmed);
    return orders;
  }, [orders, filter]);

  const totals = useMemo(() => {
    const paid = orders.filter((o) => isPaid(o.status) || o.confirmed);
    const revenue = paid.reduce((s, o) => s + (Number(o.amount_vnd) || 0), 0);
    return { count: orders.length, paidCount: paid.length, revenue };
  }, [orders]);

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', position: 'sticky', top: 0, zIndex: 100,
        background: `linear-gradient(135deg, ${BLUE} 0%, #6366f1 100%)`,
      }}>
        <button onClick={() => navigate(`/venue-dashboard/${slug}`)} style={{
          background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
          color: 'white', fontSize: 20, width: 36, height: 36, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>←</button>
        <span style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>{t('vd.orderHistory', 'Order History')}</span>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ flex: 1, maxWidth: 560, width: '100%', margin: '0 auto', padding: '14px 14px 90px' }}>
        {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>{t('common.loading', 'Loading…')}</div>}
        {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, padding: 14, fontSize: 13 }}>{err}</div>}

        {!loading && !err && (
          <>
            {/* summary */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <SummaryChip label={t('vd.orders', 'Orders')} value={totals.count} />
              <SummaryChip label={t('vd.paid', 'Paid')} value={totals.paidCount} />
              <SummaryChip label={t('vd.revenue', 'Revenue')} value={fmtVnd(totals.revenue)} wide />
            </div>

            {/* filter chips */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[['all', t('vd.all', 'All')], ['paid', t('vd.paid', 'Paid')], ['pending', t('vd.pending', 'Pending')]].map(([k, lbl]) => (
                <button key={k} onClick={() => setFilter(k)} style={{
                  padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: filter === k ? BLUE : '#f3f4f6', color: filter === k ? 'white' : '#6b7280',
                  border: filter === k ? `1px solid ${BLUE}` : '1px solid #e5e7eb',
                }}>{lbl}</button>
              ))}
            </div>

            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 12px', fontSize: 14 }}>
                {t('vd.noOrders', 'No orders yet. When customers pay through HonestLee, they appear here with their name.')}
              </div>
            )}

            {filtered.map((o) => {
              const st = STATUS_STYLE[String(o.status || '').toLowerCase()] || (o.confirmed ? STATUS_STYLE.paid : STATUS_STYLE.created);
              return (
                <div key={o.id} style={{
                  background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: 14,
                  marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        👤 {o.customer_name || '(guest)'}
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                        {fmtTime(o.created_at)}{o.table_id ? ` · ${t('vd.table', 'Table')} ${o.table_id}` : ''}{o.source ? ` · ${o.source}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 15.5, fontWeight: 800, color: '#16a34a' }}>{fmtVnd(o.amount_vnd)}</div>
                      <span style={{ background: st.bg, color: st.fg, fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, display: 'inline-block', marginTop: 3 }}>{t(st.labelKey)}</span>
                    </div>
                  </div>
                  {(o.memo || o.item_count > 0) && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f3f4f6', fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
                      {o.item_count > 0 && <span style={{ fontWeight: 700, color: '#374151' }}>{o.item_count} {t('vd.items', 'items')}</span>}
                      {o.item_count > 0 && o.memo ? ' · ' : ''}
                      {o.memo}
                    </div>
                  )}
                  {o.order_ref && (
                    <div style={{ marginTop: 6, fontSize: 10.5, color: '#cbd5e1' }}>#{o.order_ref}</div>
                  )}
                  {!o.confirmed && !isPaid(o.status) && (
                    <button
                      onClick={() => confirmPayment(o.id)}
                      disabled={confirming === o.id}
                      style={{
                        marginTop: 10, width: '100%', padding: '10px 0', borderRadius: 12, border: 'none',
                        background: confirming === o.id ? '#fbbf77' : '#16a34a', color: 'white',
                        fontSize: 13.5, fontWeight: 800, cursor: 'pointer',
                      }}>
                      {confirming === o.id ? t('vdOrders.confirming') : t('vdOrders.confirmPaymentReceived')}
                    </button>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
      <DashboardNavBar />
    </div>
  );
}

function SummaryChip({ label, value, wide }) {
  return (
    <div style={{
      flex: wide ? 1.6 : 1, background: 'white', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '10px 12px', textAlign: 'center',
    }}>
      <div style={{ fontSize: wide ? 15 : 18, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{label}</div>
    </div>
  );
}
